import { useEffect, useEffectEvent, useState } from 'react'
import { iconFor, type ReelKind } from '../../content/reelIcons'

const TICK_MS = 80
/** Each reel stops this long after the one before it. */
const STOPS_MS = [1600, 2500, 3400]
const BULBS = 18

export type Reel = { label: string; kind: ReelKind; values: string[]; target: string }

/** Reel fields; white text on each is at least 5.18:1. */
const THEME: Record<ReelKind, string> = {
  agency: '#1e40af',
  domain: '#c2410c',
  technology: '#6d28d9',
}

const BULB_COLORS = ['#fde047', '#fb641f', '#ffffff']

function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true
}

function Bulbs({ fast }: { fast: boolean }) {
  return (
    <div className="flex justify-between px-2" aria-hidden="true">
      {Array.from({ length: BULBS }, (_, i) => (
        <span
          key={i}
          className="slot-bulb inline-block size-3 rounded-full shadow-[0_0_8px_currentColor]"
          style={{
            color: BULB_COLORS[i % BULB_COLORS.length],
            background: 'currentColor',
            animationDuration: fast ? '0.35s' : '1.2s',
            animationDelay: `${(i % 3) * (fast ? 0.12 : 0.4)}s`,
          }}
        />
      ))}
    </div>
  )
}

/**
 * Three reels that spin and stop in turn on a combination the server already
 * saved: agency, domain, and technology, each in its own color with an icon
 * per item. Before the first spin the windows show a question mark; with
 * reduced motion they land at once.
 */
export default function SlotMachine({ reels, spin, onLanded }: { reels: Reel[]; spin: number; onLanded: () => void }) {
  const [elapsed, setElapsed] = useState(Number.POSITIVE_INFINITY)
  const [seenSpin, setSeenSpin] = useState(0)

  if (spin !== seenSpin) {
    setSeenSpin(spin)
    setElapsed(spin && !prefersReducedMotion() ? 0 : Number.POSITIVE_INFINITY)
  }

  const fireLanded = useEffectEvent(() => onLanded())
  useEffect(() => {
    if (!spin) return
    if (prefersReducedMotion()) {
      const done = setTimeout(fireLanded, 0)
      return () => clearTimeout(done)
    }
    const start = Date.now()
    const total = STOPS_MS[STOPS_MS.length - 1]
    const interval = setInterval(() => {
      const now = Date.now() - start
      if (now >= total) {
        clearInterval(interval)
        setElapsed(Number.POSITIVE_INFINITY)
        fireLanded()
      } else {
        setElapsed(now)
      }
    }, TICK_MS)
    return () => clearInterval(interval)
  }, [spin])

  const spinning = Number.isFinite(elapsed)

  return (
    <div
      className="flex flex-col gap-3 rounded-3xl border-4 border-[#fde047] p-4 shadow-xl"
      style={{ background: 'linear-gradient(160deg, #0a2240 0%, #1e40af 55%, #0a2240 100%)' }}
      aria-hidden="true"
    >
      <Bulbs fast={spinning} />
      <p className="text-center text-2xl font-black tracking-wide text-[#fde047] [text-shadow:0_2px_0_#a63a09]">Mission Machine</p>
      <div className="grid gap-3 sm:grid-cols-3">
        {reels.map((reel, i) => {
          const stopped = elapsed >= STOPS_MS[i]
          const value = !spin
            ? '?'
            : stopped
              ? reel.target
              : (reel.values[(Math.floor(elapsed / TICK_MS) + i * 3) % Math.max(reel.values.length, 1)] ?? '')
          const icon = value === '?' ? '❓' : iconFor(reel.kind, value)
          return (
            <div
              key={reel.label}
              className="overflow-hidden rounded-2xl border-4 border-white/80 p-4 text-center text-white shadow-inner"
              style={{ background: THEME[reel.kind] }}
            >
              <p className="text-sm font-black tracking-wide text-white/90">{reel.label}</p>
              <div
                key={stopped ? `stop-${spin}` : 'spinning'}
                className={`mt-2 flex min-h-36 flex-col items-center justify-center gap-2 ${
                  spin && !stopped ? 'slot-jitter blur-[1px]' : spin ? 'slot-pop' : ''
                }`}
              >
                <span className="text-6xl leading-none drop-shadow-[0_3px_0_rgba(0,0,0,0.25)]">{icon}</span>
                <span className="text-2xl leading-tight font-black">{value}</span>
              </div>
            </div>
          )
        })}
      </div>
      <Bulbs fast={spinning} />
    </div>
  )
}
